<template><div class="main">
  <sticky-bar
      @back="$router.push(`/s/f/${fid}`)"
      search="search by fault name or number"
      v-model="query"
      @accept="accept"
      @filter="showFilter=$event"
      :filterIcon="filterIcon"
      >
    {{ fleet && fleet.name || 'Loading...' }}
    <small>Fault Management Scheme</small>
    <template v-slot:filter>
      <div class="hflex filters" v-if="showFilter">
        <span>
          Filter by flag:
        </span>
        <div
            v-for="flag in supported"
            :key="`flag-${flag}`">
          <button
              :title="titles[flag]"
              @mousedown="toggleFilter(flag)"
              @keypress.space="toggleFilter(flag)"
              @keydown.escape="clearFilter(flag)"
              :class="{active:typeof(filter[flag])==='number'}"
              :ref="`filter-${flag}`">
            <span>{{ flag.toUpperCase() }}</span>
            <span>(</span>
            <span
                class="option"
                :class="{selected:filter[flag]===1}"
                @click="$set(filter, flag, 1)"
                @mousedown.stop>Y</span>
            <span>|</span>
            <span
                class="option"
                :class="{selected:filter[flag]===0}"
                @click="$set(filter, flag, 0)"
                @mousedown.stop>N</span>
            <span>)</span>
            <span
                class="material-icons"
                @click="clearFilter(flag)"
                @mousedown.stop>close</span>
          </button>
        </div>
        <button
            ref="clearAllFlagFilters"
            class="material-icons"
            @click="clearFilters()"
            title="Clear all flag filters"
            :disabled="!filterIcon">filter_list_off</button>
      </div>
      <div class="sticky-row" v-if="fleet">
        <span>
          Sort by:
        </span>
        <span class="id sort" @click="setSort('id')">
          Fault Code<span
            class="material-icons"
            :class="{hidden:sort!=='id'}"
            v-html="asc ? 'arrow_drop_up' : 'arrow_drop_down'" />
        </span>
        <span class="name sort" @click="setSort('name')">
          Fault Name<span
            class="material-icons"
            :class="{hidden:sort!=='name'}"
            v-html="asc ? 'arrow_drop_up' : 'arrow_drop_down'" />
        </span>
        <span class="edit-status">
          <Loading v-if="editRef" />
          <a v-else-if="edited.size" href="#" @click.prevent="saveEdits" class="error">
            Failed to save changes. Click to retry.
          </a>
        </span>
        <span class="fms-label">FMS Rev</span>
        <span class="fms-rev">{{ fleet.fms_seq }}</span>
      </div>
    </template>
  </sticky-bar>
  <div v-if="!fleet || (fmsRef !== null)" class="padded">
    <Loading />
  </div>
  <div v-else-if="fmsError" class="padded">
    <span class="error">An error occurred loading the fault management scheme.</span>
    <button @click="fetchFMS()">Try again</button>
  </div>
  <template v-else>
    <div class="faults">
      <div
          v-for="f in fmsDisplay"
          :key="`flt-${f.id}`">
        <span class="id" v-html="f.id"/>
        <span class="name" v-html="f.name"/>
        <button
            v-for="flag in supported"
            :key="`f-${flag}-${f.id}`"
            :class="{selected:f[flag]}"
            @click="toggleFlag(f, flag)"
            :disabled="!available(f,flag)"
            :title="titles[flag]">
          <span class="material-icons">done</span>
          <span>{{ flag.toUpperCase() }}</span>
        </button>
      </div>
      <Loading v-if="moreLoading" />
    </div>
  </template>
</div></template>
<script>
import { cmp_uids } from '../../../util'
import Loading from '../../Loading'
import StickyBar from '../../StickyBar'

function idCompare(a,b) {
  return a.id - b.id
}
function nameCompare(a,b) {
  a = a.name.toLowerCase()
  b = b.name.toLowerCase()
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

export default {
  name: 'fms',
  components: { Loading, StickyBar },
  props: { fid: { default: null } },
  data() {
    return {
      query: '',
      showFilter: false,
      flags: ['ca','dnl','ta','nm','cf','af','ea'],
      titles: {
        ca: 'Crew Alert',
        dnl: 'Do Not Log',
        ta: 'Transmission Allowance',
        nm: 'No Motion',
        cf: 'Critical Fault',
        af: 'Advisory Fault',
        ea: 'Email Alert',
      },
      filter: {},
      fmsRef: null,
      fms: {},
      fmsError: null,
      sort: 'id',
      asc: true,
      fmsDisplay: [],
      edited: new Set(),
      editRef: null,
    }
  },
  created() {
    this.fetchFMS()
    this.interval = setInterval(() => { this.checkScroll() }, 100)
  },
  destroyed() {
    clearInterval(this.interval)
  },
  computed: {
    fleet() {
      return (this.fid !== null) && this.$store.getters.fleets[this.fid] || null
    },
    units() { return this.fleet.units.map(uid => this.$store.state.units[uid]).filter(u => !!u) },
    supported() {
      const rv = []
      if (this.fleet.ca_supported) rv.push('ca')
      rv.push('dnl')
      if (this.fleet.ta_supported) rv.push('ta')
      rv.push('nm', 'cf', 'af', 'ea')
      return rv
    },
    filterIcon() {
      let n = 0
      for (const flag of this.flags) {
        if (flag in this.filter)
          n++
      }
      return n && `filter_${n}`
    },
    fmsFiltered() {
      let sfn
      switch (this.sort) {
      case 'id':
        if (this.asc) sfn = (a,b) => idCompare(a,b)
        else sfn = (a,b) => idCompare(b,a)
        break
      case 'name':
        if (this.asc) sfn = (a,b) => nameCompare(a,b)
        else sfn = (a,b) => nameCompare(b,a)
      }
      let ffn
      const tokens = (this.query || '').toLowerCase().split(' ').filter(a => (a !== ''))
      const flags = Object.entries(this.filter)
      if (this.query === '') {
        ffn = (a) => {
          for (const [key,val] of flags)
            if (a[key] != val)
              return false
          return true
        }
      } else if (/^[0-9]+$/.test(this.query)) {
        const intv = parseInt(this.query, 10)
        ffn = (a) => {
          if (a.id === intv) return true
          for (const [key,val] of flags)
            if (a[key] != val)
              return false
          a = a.name.toLowerCase()
          for (const t of tokens)
            if (a.indexOf(t) < 0)
              return false
          return true
        }
      } else {
        ffn = (a) => {
          for (const [key,val] of flags)
            if (a[key] != val)
              return false
          a = a.name.toLowerCase()
          for (const t of tokens)
            if (a.indexOf(t) < 0)
              return false
          return true
        }
      }
      return Object.values(this.fms).filter(ffn).sort(sfn)
    },
    moreLoading() { return this.fmsDisplay.length < this.fmsFiltered.length },
  },
  methods: {
    accept() { console.log('accept') },
    clearFilter(flag) {
      this.$delete(this.filter, flag)
      this.$refs[`filter-${flag}`][0].blur()
    },
    toggleFilter(flag) {
      let v = this.filter[flag]
      if (v === 1) this.$set(this.filter, flag, 0)
      else if (v === 0) this.$delete(this.filter, flag)
      else this.$set(this.filter, flag, 1)
    },
    clearFilters() {
      this.filter = {}
      this.$refs.clearAllFlagFilters.blur()
    },
    async fetchFMS() {
      if (this.fmsRef) {
        this.fmsRef.cancel()
        this.fmsRef = null
      }
      let ref = null
      this.fms = {}
      this.fmsDisplay = []
      this.fmsError = null
      try {
        ref = this.fmsRef = { fid: this.fid }
        let fms = {}
        for (const flt of await api.getFMS(ref))
          fms[flt.id] = flt
        this.fms = fms
      } catch(e) {
        if (ref === this.fmsRef) {
          this.fmsError = true
        }
      } finally {
        if (ref === this.fmsRef) {
          this.fmsRef = null
        }
      }
    },
    setSort(sort) {
      if (this.sort !== sort) {
        this.sort = sort
        this.asc = true
      } else {
        this.asc = !this.asc
      }
    },
    checkScroll() {
      const main = this.$el.closest('.main'),
        st = main.scrollTop,
        sh = main.scrollHeight,
        ch = main.clientHeight
      if (st + ch > sh - ch)
        this.displayMore()
    },
    displayMore() {
      const i1 = this.fmsDisplay.length, i2 = i1 + 40
      if (i1 < this.fmsFiltered.length)
        this.fmsDisplay.push(...this.fmsFiltered.slice(i1, i2))
    },
    toggleFlag(flt, flag) {
      flt[flag] = !flt[flag]
      switch (flag) {
      case 'dnl':
        if (flt[flag]) {
          if ('ta' in flt) flt['ta'] = 0
          for (const f of ['nm','cf','af','ea'])
            flt[f] = 0
        } else if ('ta' in flt) {
          flt['ta'] = 1 
        }
        break
      case 'cf':
        if (flt[flag]) flt['af'] = 0
        else flt['ea'] = 0
        break
      case 'af':
        if (flt[flag]) flt['cf'] = 0
        else flt['ea'] = 0
        break
      }
      this.edited.add(flt.id)
      this.saveEdits()
    },
    available(flt, flag) {
      switch (flag) {
      case 'ca': return true
      case 'dnl': return true
      case 'ta': return !flt['dnl']
      case 'nm': return !flt['dnl']
      case 'cf': return !flt['dnl'] && (!this.fleet.ta_supported || flt['ta'])
      case 'af': return !flt['dnl'] && (!this.fleet.ta_supported || flt['ta'])
      case 'ea': return !flt['dnl'] && (!this.fleet.ta_supported || flt['ta']) && (flt['cf'] || flt['af'])
      }
    },
    async saveEdits() {
      if (this.editRef) return // wait for previous request to complete
      try {
        let edits = {}
        for (const fid of this.edited) {
          const o = Object.assign({}, this.fms[fid])
          delete o.id
          edits[fid] = o
        }
        this.editRef = { fid: this.fid, edits }
        let fms_seq = await api.editFMS(this.editRef)
        this.$store.commit('updateFMSSeq', [ this.fleet, fms_seq ])
        let newEdited = new Set()
        for (const fid of this.edited) {
          const edit = edits[fid]
          if (!edit) {
            newEdited.add(fid)
            continue
          }
          for (const [ key, val ] of Object.entries(this.fms[fid])) {
            if (key !== 'id' && edit[key] != val) {
              newEdited.add(fid)
              break
            }
          }
        }
        this.edited = newEdited
        this.editRef = null
        if (this.edited.size) {
          this.saveEdits()
        }
      } catch(e) {
        console.error(e)
        this.editRef = null
      }
    },
  },
  watch: {
    unitColor() {
      this.saveUnitColor()
    },
    fmsFiltered() {
      this.fmsDisplay = this.fmsFiltered.slice(0, 40)
    },
  },
}
</script>
<style lang="scss" scoped>
  .hflex {
    display: flex;
    align-items: center;
    padding: 10px;
    gap: 10px;
    flex-wrap: wrap;
  }
  .filters {
    button {
      display: flex;
      align-items: center;

      &.active {
        background-color: #fff;
      }

      &:focus {
        outline: 2px solid #00f;
      }

      span.option {
        padding: 0 5px;
        font-size: 90%;
        align-self: stretch;
        &:hover {
          background-color: #eee;
        }
        display: flex;
        align-items: center;
      }

      span.material-icons {
        font-size: 120%;
        &:hover {
          background-color: #eee;
        }
      }

      span.selected {
        font-weight: bold;
        background-color: #7f7 !important;
      }


      &:not(:focus):not(.active) {
        span:not(:first-child) {
          display: none;
        }
      }
    }

  }

  .sticky-row {
    display: flex;
    background-color: #ddd;
    padding: 10px 20px;
    align-items: center;
    gap: 10px;

    .edit-status {
      flex-grow: 1;
      text-align: right;
    }
    .fms-rev {
      text-align: right;
      background-color: #fff;
      border: 1px solid #000;
      border-radius: 15px;
      padding: 5px 10px;
    }

    .sort {
      &:hover { background-color: #eee; }
      .material-icons {
        vertical-align: middle;
        margin-left: -5px;
        line-height: 0;
        display: inline-block;
        &.hidden {
          visibility: hidden;
        }
      }
    }
  }

  .faults {
    display: inline-flex;
    flex-direction: column;
    padding: 5px 0;
    margin-right: auto;
    > div {
      display: inline-flex;
      flex-wrap: wrap;
      gap: 10px;
      align-items: center;
      justify-content: right;
      padding: 5px 20px;
      span:nth-child(2) {
        flex-grow: 1;
        white-space: nowrap;
      }
      &:nth-child(even) {
        background-color: #ddf;
      }
      button {
        position: relative;
        width: 50px;
        height: 50px;
        span {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .material-icons {
          font-size: 60px;
          color: #4f4;
          display: none;
        }
        &.selected {
          outline: 2px solid #0c4;
          background-color: #fff;
          font-weight: bold;
          .material-icons {
            display: flex;
          }
        }
        &:focus {
          outline: 2px solid #0af;
        }
      }
    }
  }
</style>
